// src/helper/apiError.helpers.ts

export type ApiErrorLike = {
  message?: string
  code?: string
  status?: number
  errors?: Record<string, string[]>
}

export type ApiErrorInit = ApiErrorLike & {
  message: string
  data?: unknown
  /** Name of the file that caused the error, for attachment uploads. */
  fileName?: string
}

/**
 * Builds an Error carrying the backend's error payload, matching the shape
 * thrown by the shared `API()` wrapper.
 */
export function createApiError(init: ApiErrorInit): Error & ApiErrorInit {
  return Object.assign(new Error(init.message), init)
}

/** Extracts a user-facing message from an unknown thrown value. */
export function getErrorMessage(
  error: unknown,
  fallback = 'Ein unerwarteter Fehler ist aufgetreten.'
): string {
  if (error instanceof Error && error.message) return error.message

  if (typeof error === 'object' && error !== null) {
    const message = (error as ApiErrorLike).message
    if (message) return message
  }

  return fallback
}

export type UiError = {
  title?: string
  message: string
  fieldErrors?: Record<string, string>
  code?: string
}

const API_ERROR_MESSAGES_DE: Readonly<Record<string, string>> = {
  ACTIVITIES_FETCH_ERROR: 'Die Aktivitäten konnten nicht abgerufen werden.',
  ACTIVITY_CREATE_ERROR: 'Die Aktivität konnte nicht gespeichert werden.',
  ACTIVITY_UPDATE_ERROR: 'Die Aktivität konnte nicht aktualisiert werden.',
  ACTIVITY_DELETE_ERROR: 'Die Aktivität konnte nicht gelöscht werden.',
  ACTIVITY_ATTACHMENTS_LIST_ERROR: 'Die Anhänge der Aktivität konnten nicht abgerufen werden.',
  ACTIVITY_ATTACHMENT_DELETE_ERROR: 'Der Anhang konnte nicht gelöscht werden.',

  INVOICES_FETCH_ERROR: 'Die Rechnungen konnten nicht abgerufen werden.',
  INVOICE_FETCH_ERROR: 'Die Rechnung konnte nicht abgerufen werden.',
  INVOICE_CREATE_DRAFT_ERROR: 'Der Rechnungsentwurf konnte nicht angelegt werden.',
  INVOICE_UPDATE_DRAFT_ERROR: 'Der Rechnungsentwurf konnte nicht aktualisiert werden.',
  INVOICE_DELETE_DRAFT_ERROR: 'Der Rechnungsentwurf konnte nicht gelöscht werden.',
  INVOICE_FINALIZE_ERROR: 'Die Rechnung konnte nicht erstellt werden.',
  INVOICE_SEND_ERROR: 'Die Rechnung konnte nicht versandt werden.',
  INVOICE_CANCEL_ERROR: 'Die Rechnung konnte nicht storniert werden.',
  INVOICE_REGENERATE_ERROR: 'Die Rechnung konnte nicht erneut erstellt werden.',
  INVOICE_SUBMIT_ERROR: 'Die Rechnung konnte nicht zur Freigabe eingereicht werden.',
  INVOICE_APPROVE_ERROR: 'Die Rechnung konnte nicht freigegeben werden.',
  INVOICE_REJECT_ERROR: 'Die Rechnung konnte nicht abgelehnt werden.',
  INVOICE_HISTORY_FETCH_ERROR: 'Der Rechnungsverlauf konnte nicht abgerufen werden.',
  INVOICE_UPDATE_ITEMS_ERROR: 'Die Rechnungspositionen konnten nicht aktualisiert werden.',
}

export function translateApiErrorCode(code?: string | null): string | undefined {
  if (!code) return undefined
  return API_ERROR_MESSAGES_DE[code.trim().toUpperCase()]
}

export function translateBackendUnavailableReason(reason?: string | null): string {
  const normalized = (reason ?? '').trim()

  if (
    /failed to fetch/i.test(normalized) ||
    /network\s*error/i.test(normalized) ||
    /networkerror/i.test(normalized) ||
    /load failed/i.test(normalized)
  ) {
    return 'Die Verbindung zu den zentralen Systemen konnte nicht hergestellt werden.'
  }

  const httpStatus = normalized.match(/^HTTP\s+(\d{3})$/i)?.[1]

  if (httpStatus === '500') {
    return 'Die zentralen Systeme haben einen internen Fehler gemeldet (HTTP 500).'
  }
  if (httpStatus === '502') {
    return 'Die Verbindung zu den zentralen Systemen ist vorübergehend gestört (HTTP 502).'
  }
  if (httpStatus === '503') {
    return 'Die zentralen Systeme sind vorübergehend nicht verfügbar (HTTP 503).'
  }
  if (httpStatus === '504') {
    return 'Die Anfrage an die zentralen Systeme hat das Zeitlimit überschritten (HTTP 504).'
  }
  if (httpStatus) {
    return `Die zentralen Systeme haben einen Fehler gemeldet (HTTP ${httpStatus}).`
  }

  return 'Ein technischer Verbindungsfehler ist aufgetreten.'
}

function normalizeMessage(value?: string) {
  return (value || '').trim().toLowerCase()
}

function firstFieldError(errors?: Record<string, string[]>, field?: string) {
  if (!errors || !field) return undefined
  return errors[field]?.[0]
}

export function mapAuthError(error: ApiErrorLike): UiError {
  const msg = normalizeMessage(error.message)

  // Signup: duplicate account
  if (
    msg.includes('username or email already exists') ||
    msg.includes('email already exists') ||
    msg.includes('user already exists')
  ) {
    return {
      title: 'Registrierung nicht möglich',
      message: 'Ein Konto mit dieser E-Mail-Adresse existiert bereits.',
      code: 'AUTH_DUPLICATE_USER',
    }
  }

  // Password reset: password reused
  if (
    msg.includes('password cannot be reused') ||
    msg.includes('cannot be reused') ||
    msg.includes('password reuse')
  ) {
    return {
      title: 'Kennwort ungültig',
      message:
        'Das neue Kennwort darf nicht mit einem bereits verwendeten Kennwort übereinstimmen.',
      fieldErrors: {
        password:
          'Das neue Kennwort darf nicht mit einem bereits verwendeten Kennwort übereinstimmen.',
      },
      code: 'AUTH_PASSWORD_REUSED',
    }
  }

  // Password reset: generic validation payload
  if (error.code === 'INVALID_PAYLOAD') {
    const passwordError = firstFieldError(error.errors, 'password')
    if (passwordError) {
      return {
        title: 'Eingaben prüfen',
        message: translateValidationMessage(passwordError),
        fieldErrors: {
          password: translateValidationMessage(passwordError),
        },
        code: 'AUTH_INVALID_PAYLOAD',
      }
    }

    return {
      title: 'Eingaben prüfen',
      message: 'Die Eingaben sind ungültig. Bitte überprüfen Sie Ihre Angaben.',
      code: 'AUTH_INVALID_PAYLOAD',
    }
  }

  // expired / invalid token
  if (msg.includes('expired') || msg.includes('invalid')) {
    return {
      title: 'Link ungültig',
      message: 'Der Zurücksetzen-Link ist abgelaufen oder ungültig.',
      code: 'AUTH_RESET_LINK_INVALID',
    }
  }

  // generic backend validation text that should not leak in English
  if (msg.includes('the request cannot be processed')) {
    return {
      title: 'Anfrage nicht möglich',
      message: 'Die Anfrage konnte nicht verarbeitet werden. Bitte überprüfen Sie Ihre Eingaben.',
      code: 'AUTH_REQUEST_NOT_PROCESSABLE',
    }
  }

  return {
    title: 'Fehler',
    message: error.message || 'Ein unerwarteter Fehler ist aufgetreten.',
    code: error.code,
  }
}

export function translateValidationMessage(message: string) {
  const msg = normalizeMessage(message)

  if (msg.includes('password cannot be reused')) {
    return 'Das neue Kennwort darf nicht mit einem bereits verwendeten Kennwort übereinstimmen.'
  }

  if (msg.includes('must be at least')) {
    return 'Das Kennwort erfüllt die Mindestanforderungen nicht.'
  }

  return message
}
